import { useState, useEffect, useMemo } from 'react';
import {
    Box,
    CircularProgress,
    Container,
    Typography,
    Chip,
    Stack,
    Button,
    Snackbar,
    Alert,
} from '@mui/material';
import OrderDetailDrawer from '../admin/components/OrderDetailDrawer';
import OrdersTable from '../admin/components/OrdersTable';
import OrdersHeader from '../admin/components/OrdersHeader';
import ManualApplicationDialog from '../admin/components/ManualApplicationDialog';
import { useApplications } from './hooks/useApplications';
import type { Application } from './types/application';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import DownloadIcon from '@mui/icons-material/Download';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { STATUS_LIST } from '../../constants/statusList';
import {
    INFLOW_CHANNEL_VALUES,
    INFLOW_BUCKET_UNKNOWN,
    INFLOW_BUCKET_MANUAL,
    getInflowBucket,
    getInflowLabel,
} from '../../constants/inflowChannels';
import { getAuth } from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';

const OrdersPage = () => {
    const { applications, loading } = useApplications();
    const navigate = useNavigate();
    const [selectedApp, setSelectedApp] = useState<Application | null>(null);

    const [searchParams] = useSearchParams();
    const statusParam = searchParams.get('status') ?? '전체';

    const validStatus = ['전체', '진행중', ...STATUS_LIST];
    const initialStatus = validStatus.includes(statusParam) ? statusParam : '전체';

    const [statusFilter, setStatusFilter] = useState(initialStatus);
    const [searchFilter, setSearchFilter] = useState('');
    const [dateRange, setDateRange] = useState('전체');
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);

    const [adminEmail, setAdminEmail] = useState('');
    const [selectedIds, setSelectedIds] = useState<string[]>([]);

    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success',
    });

    // 🆕 수동 신청 다이얼로그 상태
    const [showManualDialog, setShowManualDialog] = useState(false);

    // 🆕 특수 필터 상태
    const [showIncomplete, setShowIncomplete] = useState(false);
    const [showManualOnly, setShowManualOnly] = useState(false);
    const [showIndividualOnly, setShowIndividualOnly] = useState(false);

    // 📈 유입경로 필터 상태
    const [inflowFilter, setInflowFilter] = useState('전체');

    // 📈 유입경로 필터 옵션 (실제 데이터에 존재하는 버킷만, 알려진 채널 → 커스텀 → 수동등록 → 미수집 순)
    const inflowOptions = useMemo(() => {
        const present = new Set(applications.map(getInflowBucket));
        const known = INFLOW_CHANNEL_VALUES.filter((v) => present.has(v));
        const custom = [...present]
            .filter((b) =>
                !INFLOW_CHANNEL_VALUES.includes(b as typeof INFLOW_CHANNEL_VALUES[number]) &&
                b !== INFLOW_BUCKET_UNKNOWN &&
                b !== INFLOW_BUCKET_MANUAL)
            .sort();
        return ['전체', ...known, ...custom,
            ...(present.has(INFLOW_BUCKET_MANUAL) ? [INFLOW_BUCKET_MANUAL] : []),
            ...(present.has(INFLOW_BUCKET_UNKNOWN) ? [INFLOW_BUCKET_UNKNOWN] : []),
        ].map((v) => ({ value: v, label: v === '전체' ? '전체' : getInflowLabel(v) }));
    }, [applications]);

    useEffect(() => {
        setStatusFilter(initialStatus);
        const auth = getAuth();
        const user = auth.currentUser;
        if (user && user.email) {
            setAdminEmail(user.email);
        }
    }, [initialStatus]);

    const handleSelect = (app: Application) => {
        // 🚀 새로운 상세 페이지로 이동
        navigate(`/admin/applications/${app.id}`);
    };

    const handleBatchPickup = async () => {
        if (selectedIds.length === 0) {
            setSnackbar({
                open: true,
                message: '선택된 신청서가 없습니다.',
                severity: 'error',
            });
            return;
        }

        const confirm = window.confirm(`총 ${selectedIds.length}건을 '택배접수' 상태로 변경하시겠습니까?`);
        if (!confirm) return;

        try {
            const batch = selectedIds.map(async (id) => {
                const appRef = doc(db, 'applications', id);
                await updateDoc(appRef, {
                    status: '택배접수',
                    updated_at: new Date()
                });
            });
            await Promise.all(batch);
            setSnackbar({
                open: true,
                message: '선택된 신청서가 모두 택배접수 상태로 변경되었습니다.',
                severity: 'success',
            });
            setSelectedIds([]);
        } catch (error) {
            console.error('상태 변경 오류:', error);
            setSnackbar({
                open: true,
                message: '상태 변경 중 오류가 발생했습니다.',
                severity: 'error',
            });
        }
    };

    // 🆕 수동 신청 성공 핸들러
    const handleManualApplicationSuccess = (applicationId: string, applicationNumber: number) => {
        setSnackbar({
            open: true,
            message: `✅ 수동 신청이 등록되었습니다. (신청번호: ${applicationNumber})`,
            severity: 'success',
        });
        // 목록 새로고침을 위해 페이지 리로드 또는 상태 업데이트
        // useApplications 훅이 실시간 구독이면 자동으로 반영됨
    };

    const now = new Date();

    const isInSelectedDateRange = (createdAt: Date) => {
        if (dateRange === '전체') return true;
        if (dateRange === '최근 7일') {
            const sevenDaysAgo = new Date(now);
            sevenDaysAgo.setDate(now.getDate() - 7);
            return createdAt >= sevenDaysAgo;
        }
        if (dateRange === '이번 달') {
            return (
                createdAt.getFullYear() === now.getFullYear() &&
                createdAt.getMonth() === now.getMonth()
            );
        }
        if (dateRange === '사용자 지정') {
            if (!startDate || !endDate) return true;
            const start = new Date(startDate);
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            return createdAt >= start && createdAt <= end;
        }
        return true;
    };

    const filteredApplications = applications.filter((app) => {
        const createdAt = app.created_at?.seconds
            ? new Date(app.created_at.seconds * 1000)
            : new Date();

        const statusMatch =
            statusFilter === '전체'
                ? true
                : statusFilter === '진행중'
                    ? !['완료', '반송'].includes(app.status)
                    : app.status === statusFilter;

        const searchMatch =
            app.user_name.includes(searchFilter) ||
            app.phone_number.includes(searchFilter) ||
            (app.application_number && String(app.application_number).includes(searchFilter)) || // ✅ 신청번호 추가
            (app.devices && app.devices.some(d => d.deviceModel.includes(searchFilter))) ||
            (app.store_code && app.store_code.includes(searchFilter)); // ✅ 매장 코드(store_code) 추가

        const dateMatch = isInSelectedDateRange(createdAt);

        // 특수 필터 매칭
        const incompleteMatch = showIncomplete ? app.is_incomplete === true : true;
        const manualMatch = showManualOnly 
            ? (app.source_type === 'manual' || app.source_type === 'partner') 
            : true;
        // 일반 접수(B2C 개인고객) 필터: channel이 'b2c'인 건만 표시
        const individualMatch = showIndividualOnly ? app.channel === 'b2c' : true;

        // 📈 유입경로 필터
        const inflowMatch = inflowFilter === '전체' ? true : getInflowBucket(app) === inflowFilter;

        return statusMatch && searchMatch && dateMatch && incompleteMatch && manualMatch && individualMatch && inflowMatch;
    });

    const handleDownloadExcel = () => {
        const exportData = filteredApplications.map((app) => ({
            신청번호: app.application_number,
            이름: app.user_name,
            전화번호: app.phone_number,
            기기종류: app.devices?.map(d => d.deviceType).join(', ') ?? '-', // ✅ 기기 종류만
            기기상세: app.devices?.map(d => d.deviceModel).join(', ') ?? '-', // ✅ 기기 상세만
            신청일: app.created_at?.seconds
                ? new Date(app.created_at.seconds * 1000).toLocaleDateString()
                : '-',
            상태: app.status,
            송장번호: app.tracking_number ?? '',
            검수금액: app.price_offer ?? '',
            최종금액: app.final_price ?? '',
            주소1: app.pickup_address ?? '',
            주소2: app.pickup_address_detail ?? '',
            비고: app.remark ?? '',
            채널: app.channel === 'b2c' ? '개인' : '거래처',
            유입경로: getInflowLabel(getInflowBucket(app)),
            유입캠페인: app.inflow?.campaign ?? '',
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, '신청서 목록');

        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });

        const now = new Date();
        const yyyy = now.getFullYear();
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const dd = String(now.getDate()).padStart(2, '0');
        const fileName = `${yyyy}_${mm}-${dd}_신청서내역.xlsx`;

        saveAs(blob, fileName);
    };


    if (loading) {
        return (
            <Box textAlign="center" mt={6}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth={false} sx={{ py: 4 }}>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
                spacing={2}
                sx={{ mb: 2 }}
            >
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h6" fontWeight="bold">
                        📦 신청서 목록
                    </Typography>
                    <Chip
                        label={`전체 ${applications.length}건 / 필터 ${filteredApplications.length}건`}
                        color="primary"
                        variant="outlined"
                        sx={{
                            fontSize: 13,
                            fontWeight: 'bold',
                            height: 28,
                            px: 1.5,
                            borderRadius: 1.5,
                        }}
                    />
                </Stack>

                {/* 우측: 버튼 그룹 */}
                <Stack direction="row" spacing={1}>
                    {/* 🆕 수동 신청 버튼 */}
                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={() => setShowManualDialog(true)}
                        sx={{ whiteSpace: 'nowrap', px: 2, fontWeight: 500 }}
                    >
                        수동 신청
                    </Button>

                    <Button
                        variant="contained"
                        color="success"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadExcel}
                        sx={{ whiteSpace: 'nowrap', px: 2, fontWeight: 500 }}
                    >
                        Excel
                    </Button>

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleBatchPickup}
                        sx={{ whiteSpace: 'nowrap', px: 2, fontWeight: 500 }}
                    >
                        택배 접수하기
                    </Button>
                </Stack>
            </Stack>

            <OrdersHeader
                selectedStatus={statusFilter}
                onStatusChange={setStatusFilter}
                onSearchChange={setSearchFilter}
                selectedDateRange={dateRange}
                onDateRangeChange={setDateRange}
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={setStartDate}
                onEndDateChange={setEndDate}
                showIncomplete={showIncomplete}
                onShowIncompleteChange={setShowIncomplete}
                showManualOnly={showManualOnly}
                onShowManualOnlyChange={setShowManualOnly}
                showIndividualOnly={showIndividualOnly}
                onShowIndividualOnlyChange={setShowIndividualOnly}
                selectedInflow={inflowFilter}
                onInflowChange={setInflowFilter}
                inflowOptions={inflowOptions}
            />

            <OrdersTable
                applications={filteredApplications}
                selectedIds={selectedIds}
                onSelectIdsChange={setSelectedIds}
                onSelect={handleSelect}
            />

            <OrderDetailDrawer
                open={Boolean(selectedApp)}
                application={selectedApp}
                adminId={adminEmail}
                onClose={() => setSelectedApp(null)}
            />

            {/* 🆕 수동 신청 다이얼로그 */}
            <ManualApplicationDialog
                open={showManualDialog}
                onClose={() => setShowManualDialog(false)}
                onSuccess={handleManualApplicationSuccess}
                adminEmail={adminEmail}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default OrdersPage;
